import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  styled,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Input = styled('input')({
  display: 'none',
});

function MembersManagement() {
  const theme = useTheme();
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedMember, setSelectedMember] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    dateOfEnrollment: '',
    enrollmentNo: '',
    address: '',
    mobileNo: ''
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      dateOfEnrollment: '',
      enrollmentNo: '',
      address: '',
      mobileNo: ''
    });
    setSelectedMember(null);
    setDialogMode('add');
  };

  const handleOpenDialog = (member = null) => {
    if (member) {
      setDialogMode('edit');
      setSelectedMember(member);
      setFormData({
        name: member.name || '',
        dateOfEnrollment: member.dateOfEnrollment || '',
        enrollmentNo: member.enrollmentNo || '',
        address: member.address || '',
        mobileNo: member.mobileNo || ''
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const fetchMembers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'members'));
      const membersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMembers(membersData);
    } catch (error) {
      setError('Error fetching members: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rowsPerPageOptions = [25, 50, 100, 250, 500];

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveMember = async () => {
    try {
      if (dialogMode === 'edit' && selectedMember) {
        // Update existing member
        const memberRef = doc(db, 'members', selectedMember.id);
        await updateDoc(memberRef, formData);
      } else {
        // Add new member
        await addDoc(collection(db, 'members'), formData);
      }
      handleCloseDialog();
      await fetchMembers();
      setSuccess('Member saved successfully!');
    } catch (error) {
      setError('Error saving member: ' + error.message);
    }
  };

  const handleDelete = async (member) => {
    try {
      await deleteDoc(doc(db, 'members', member.id));
      await fetchMembers();
      setSuccess('Member deleted successfully!');
    } catch (error) {
      setError('Error deleting member: ' + error.message);
    }
  };

  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(members.map(member => ({
        Name: member.name,
        'Date of Enrollment': member.dateOfEnrollment,
        'Enrollment No': member.enrollmentNo,
        Address: member.address,
        'Mobile No': member.mobileNo
      })));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Members');

      // Generate buffer
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Save file
      saveAs(data, `ACBA_Members_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      setSuccess('Members list exported successfully!');
    } catch (error) {
      setError('Error exporting members: ' + error.message);
    }
  };

  const handleImportExcel = async (event) => {
    try {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(worksheet);

          if (!data || data.length === 0) {
            throw new Error('Excel file is empty or could not be parsed');
          }

          const requiredFields = ['Name', 'Enrollment No', 'Date of Enrollment', 'Mobile No'];
          const validRows = [];
          const invalidRows = [];
          
          // Get existing members first
          setLoading(true);
          const membersRef = collection(db, 'members');
          const snapshot = await getDocs(membersRef);
          const existingMembers = {};
          snapshot.forEach(doc => {
            const member = doc.data();
            existingMembers[member.enrollmentNo] = { id: doc.id, ...member };
          });
          
          // Validate rows
          data.forEach((row, index) => {
            const rowNumber = index + 1;
            const missingFields = requiredFields.filter(field => !row[field]);
            
            if (missingFields.length > 0) {
              invalidRows.push({
                rowNumber,
                missingFields,
                data: row
              });
            } else {
              validRows.push({
                ...row,
                isNew: !existingMembers[row['Enrollment No']]
              });
            }
          });

          // Handle validation errors
          if (invalidRows.length > 0) {
            const errorSummary = {
              missingEnrollment: invalidRows.filter(r => r.missingFields.includes('Enrollment No')).length,
              missingDate: invalidRows.filter(r => r.missingFields.includes('Date of Enrollment')).length,
              missingMobile: invalidRows.filter(r => r.missingFields.includes('Mobile No')).length,
              missingName: invalidRows.filter(r => r.missingFields.includes('Name')).length
            };

            const errorMessage = `Found ${invalidRows.length} rows with missing data:
- ${errorSummary.missingEnrollment} rows missing Enrollment No
- ${errorSummary.missingDate} rows missing Date of Enrollment
- ${errorSummary.missingMobile} rows missing Mobile No
- ${errorSummary.missingName} rows missing Name

Click "View Details" to see the affected rows.
Please fix these issues in the Excel file and try again.`;

            setErrorDetails({
              summary: errorSummary,
              invalidRows: invalidRows.map(row => ({
                rowNumber: row.rowNumber,
                name: row.data['Name'] || 'N/A',
                missingFields: row.missingFields.join(', '),
                type: 'missing'
              }))
            });
            setShowErrorDialog(true);
            throw new Error(errorMessage);
          }

          // Process valid rows
          let updatedCount = 0;
          let addedCount = 0;

          for (const row of validRows) {
            const memberData = {
              name: row['Name'],
              dateOfEnrollment: row['Date of Enrollment'],
              enrollmentNo: row['Enrollment No'],
              address: row['Address'] || '',
              mobileNo: row['Mobile No']
            };

            const existing = existingMembers[memberData.enrollmentNo];
            if (existing) {
              await updateDoc(doc(db, 'members', existing.id), memberData);
              updatedCount++;
            } else {
              await addDoc(membersRef, memberData);
              addedCount++;
            }
          }

          await fetchMembers();
          setSuccess(`Import successful! Added ${addedCount} new members and updated ${updatedCount} existing members.`);
          event.target.value = '';
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      };

      reader.onerror = (error) => {
        setError('Error reading file: ' + error);
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      setError('Error importing members: ' + error.message);
    }
  };

  const filteredMembers = members.filter(member =>
    Object.values(member).some(value =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        pl: { xs: 0, sm: '240px' },
        pt: { xs: 1, sm: 2 },
        pb: { xs: 1, sm: 2 },
        width: '100%',
        height: 'calc(100vh - 64px)',
        bgcolor: '#f8f9fa',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="xl" sx={{ height: '100%' }}>
        {/* Header with Export/Import buttons */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: '#580100',
            }}
          >
            Members Management
          </Typography>
          <Stack direction="row" spacing={2}>
            <Input
              accept=".xlsx,.xls"
              id="import-excel"
              type="file"
              onChange={handleImportExcel}
            />
            {/* <Tooltip title="Import Members from Excel">
              <label htmlFor="import-excel">
                <Button
                  component="span"
                  startIcon={<FileUploadIcon />}
                  sx={{
                    color: '#580100',
                    borderColor: '#580100',
                    '&:hover': { borderColor: '#3d0000', bgcolor: alpha('#580100', 0.04) }
                  }}
                  variant="outlined"
                >
                  Import Excel
                </Button>
              </label>
            </Tooltip> */}
            {/* <Tooltip title="Export Members to Excel">
              <Button
                onClick={handleExportExcel}
                startIcon={<FileDownloadIcon />}
                sx={{
                  color: '#580100',
                  borderColor: '#580100',
                  '&:hover': { borderColor: '#3d0000', bgcolor: alpha('#580100', 0.04) }
                }}
                variant="outlined"
              >
                Export Excel
              </Button>
            </Tooltip> */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#580100',
                '&:hover': { bgcolor: '#3d0000' },
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Add Member
            </Button>
          </Stack>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search members..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 300,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
              }
            }}
          />
        </Box>

        {/* Table */}
        <Paper 
          elevation={0}
          sx={{ 
            height: 'calc(100% - 120px)',
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          <TableContainer sx={{ height: 'calc(100% - 52px)', overflowY: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#580100', color: 'white', fontSize: '0.8rem' }}>Sr. No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#580100', color: 'white', fontSize: '0.8rem' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#580100', color: 'white', fontSize: '0.8rem' }}>Date of Enrollment</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#580100', color: 'white', fontSize: '0.8rem' }}>Enrollment No</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#580100', color: 'white', fontSize: '0.8rem' }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#580100', color: 'white', fontSize: '0.8rem' }}>Mobile No</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#580100', color: 'white', fontSize: '0.8rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMembers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((member, index) => (
                    <TableRow key={member.id} hover>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{member.name}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{member.dateOfEnrollment}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{member.enrollmentNo}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{member.address}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{member.mobileNo}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          color="primary"
                          sx={{ mr: 1 }}
                          onClick={() => handleOpenDialog(member)}
                        >
                          <EditIcon sx={{ fontSize: '1.2rem' }} />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDelete(member)}
                        >
                          <DeleteIcon sx={{ fontSize: '1.2rem' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredMembers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              '.MuiTablePagination-select': {
                fontSize: '0.75rem'
              },
              '.MuiTablePagination-displayedRows': {
                fontSize: '0.75rem'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '0.75rem'
              },
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: 2
            }}
          />
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ color: theme.palette.error.main }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this member?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setOpenDeleteDialog(false)}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(selectedMember)}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Member Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            pb: 1,
            color: '#580100',
            fontWeight: 600
          }}>
            {dialogMode === 'edit' ? 'Edit Member' : 'Add New Member'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                size="small"
              />
              <TextField
                fullWidth
                label="Date of Enrollment"
                name="dateOfEnrollment"
                type="date"
                value={formData.dateOfEnrollment}
                onChange={handleInputChange}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Enrollment No"
                name="enrollmentNo"
                value={formData.enrollmentNo}
                onChange={handleInputChange}
                size="small"
              />
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                size="small"
                multiline
                rows={3}
              />
              <TextField
                fullWidth
                label="Mobile No"
                name="mobileNo"
                value={formData.mobileNo}
                onChange={handleInputChange}
                size="small"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveMember}
              sx={{
                bgcolor: '#580100',
                '&:hover': { bgcolor: '#3d0000' },
                textTransform: 'none'
              }}
            >
              {dialogMode === 'edit' ? 'Save Changes' : 'Add Member'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error Details Dialog */}
        <Dialog 
          open={showErrorDialog} 
          onClose={() => setShowErrorDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            Excel Import Errors
            <IconButton
              aria-label="close"
              onClick={() => setShowErrorDialog(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'grey.500'
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {errorDetails && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2, color: 'error.main' }}>
                  The following rows have missing required fields:
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Missing Fields</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorDetails.invalidRows.map((row) => (
                        <TableRow key={row.rowNumber}>
                          <TableCell>{row.rowNumber}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.missingFields}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    To fix these issues:
                  </Typography>
                  <ol style={{ marginTop: 8, paddingLeft: 24 }}>
                    <li>Make sure all required fields are filled in</li>
                    <li>Try importing again</li>
                  </ol>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowErrorDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default MembersManagement;
